<template>
    <ModalShell title="Add Child" subtitle="Enter your child's information to add them to your protection plan.">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="4.5" r="2.5" /><path d="m10.2 15.5-1.7 4.7a.7.7 0 0 0 .7.8h5.6a.7.7 0 0 0 .7-.8l-1.7-4.7" /><path d="M15.5 10a3.5 3.5 0 1 0-7 0v5.5h7z" />
            </svg>
        </template>
        <!-- Form fields -->
        <div class="form-section">
            <!-- First Name and Last Name row -->
            <div class="form-row">
                <ModalField class="flex-1" label="First Name" required input-id="add-child-first-name">
                    <PrimeInputText
                        id="add-child-first-name"
                        v-model="firstName"
                        class="w-full"
                        placeholder="First name"
                        aria-required="true"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </ModalField>
                <ModalField class="flex-1" label="Last Name" required input-id="add-child-last-name">
                    <PrimeInputText
                        id="add-child-last-name"
                        v-model="lastName"
                        class="w-full"
                        placeholder="Last name"
                        aria-required="true"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </ModalField>
            </div>

            <!-- Date of Birth -->
            <ModalField
                label="Date of Birth"
                required
                input-id="add-child-dob"
                :error="dobError || undefined"
                v-slot="{ errorId, invalid }"
            >
                <!-- DatePicker's $attrs land on its wrapper span, so the input's own ARIA has
                     to be routed through the pcInputText pass-through section. -->
                <PrimeDatePicker
                    v-model="dob"
                    inputId="add-child-dob"
                    class="w-full"
                    placeholder="Select date of birth"
                    dateFormat="mm/dd/yy"
                    :maxDate="maxDate"
                    :invalid="invalid"
                    :pt="{
                        pcInputText: {
                            root: {
                                'aria-required': 'true',
                                'aria-describedby': errorId,
                            },
                        },
                    }"
                />
            </ModalField>
        </div>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog"
            />
            <PrimeButton
                label="Add Child"
                variant="primary"
                :disabled="!canSubmit"
                :loading="isSubmitting"
                @click="onSubmit"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import PrimeDatePicker from 'primevue/datepicker';
import ModalField from '~/components/modals/shared/ModalField.vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useContactsStore } from '~/stores/person/contacts';
import { ModalNames } from '~/types/modals';

const { letterOnlyInputHandler } = useUtils();
import { calculateAge } from '~/utils/age';
const contactsStore = useContactsStore();

const dialogRef = getDialogRef(ModalNames.ADD_CHILD_FORM);

// Form state
const firstName = ref('');
const lastName = ref('');
const dob = ref<Date | null>(null);
const isSubmitting = ref(false);

// Max date = today (child must already be born)
const maxDate = new Date();

const dobError = computed(() => {
    if (!dob.value) return '';

    const age = calculateAge(dob.value);

    if (age !== null && age >= 18) {
        return 'Child must be under 18 years old. Use "Protect Another Adult" for adults.';
    }

    return '';
});

const canSubmit = computed(() => {
    return firstName.value.trim().length > 0 &&
           lastName.value.trim().length > 0 &&
           dob.value !== null &&
           !dobError.value &&
           !isSubmitting.value;
});

const closeDialog = () => {
    dialogRef.value.close();
};

const onEnter = (e: KeyboardEvent) => {
    e.preventDefault();
    if (canSubmit.value) {
        onSubmit();
    }
};

async function onSubmit() {
    if (!canSubmit.value) return;

    isSubmitting.value = true;

    // Format DOB as YYYY-MM-DD
    const birthDate = new Date(dob.value!);
    const formattedDob = `${birthDate.getFullYear()}-${String(birthDate.getMonth() + 1).padStart(2, '0')}-${String(birthDate.getDate()).padStart(2, '0')}`;

    const success = await contactsStore.createMinorChild({
        first_name: firstName.value.trim(),
        last_name: lastName.value.trim(),
        dob: formattedDob,
    });

    if (success) {
        closeDialog();
    }

    isSubmitting.value = false;
}
</script>

<style lang="scss" scoped>
.form-section {
    @apply flex flex-col gap-4 w-full;
}

.form-row {
    @apply flex sm:flex-row flex-col gap-4;
}

</style>
